import React from "react";
import {
    Col,
    Row,
    Select,
    Form,
    InputNumber,
    Switch,
    Input,
} from "antd";
import { useSelector } from "react-redux";
import { RectTablePropertiesEnum } from "../../../enums"
import { SEAT_LABEL_OPTIONS, SeatDistribution } from "../../../enums/SeatDistributionEnum"
import { SelectionTypeEnum, SELECTION_OPTIONS } from "../../../enums/SelectionTypeEnum"
import { tPropertyBar, tTable } from "../../../assets/libs/i18n/translator";
import { CategorySelect } from "./CategorySelect";
import { DistributionSelect } from "./DistributionSelect";
import { StartsAtInput } from "./StartsAtInput";


const RectTablePropertiesPanel = ({ form, getFieldValue, selectedItem: rectTable }) => {
    const { Option } = Select;
    const { Item } = Form;
    const { tableLabeling, seatsQuantity, maxSeats, category, selectionType, section: { label }, seatLabeling } = rectTable;
    const categories = useSelector((state) => state.properties.categories);
    const table = rectTable._objects[0]._objects[0];

    // Read the currently selected distribution from the form so the startsAt
    // input reflects the choice immediately, before it is persisted.
    const seatDistribution =
        getFieldValue?.(RectTablePropertiesEnum.SeatLabelingType) ?? (seatLabeling.key ?? SeatDistribution.NONE);

    return (
        <>
            <Row>
                <Col span={24} className="mb-1">
                    <h3 className="text-center">{tTable("tables")}</h3>
                </Col>
            </Row>
            <Row>
                <Col span={24} className="mb-1">
                    <h3><b>{tPropertyBar("category")}</b></h3>
                </Col>
                <Col span={24} className="mb-1">
                    <Item wrapperCol={{ span: 24 }}>
                        {form("category", {
                            initialValue: category?.key,
                        })(
                            <CategorySelect categories={categories} />
                        )}
                    </Item>
                </Col>
            </Row>
            <Row>
                <Col span={24} className="mb-1 mt-2">
                    <h3><b>{tPropertyBar("seats")}</b></h3>
                </Col>

                <Col span={24}>
                    <Row gutter={4}>

                        <Col span={12}>
                            <Item label={tTable("up")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                                {form(RectTablePropertiesEnum.SeatsQuantityUp, {
                                    initialValue: seatsQuantity.up ?? 0,
                                })(<InputNumber min={0} max={maxSeats.horizontal} />)}
                            </Item>
                        </Col>

                        <Col span={12}>
                            <Item label={tTable("down")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                                {form(RectTablePropertiesEnum.SeatsQuantityDown, {
                                    initialValue: seatsQuantity.down ?? 0,
                                })(<InputNumber min={0} max={maxSeats.horizontal} />)}
                            </Item>
                        </Col>

                        <Col span={12}>
                            <Item label={tTable("left")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                                {form(RectTablePropertiesEnum.SeatsQuantityLeft, {
                                    initialValue: seatsQuantity.left ?? 0,
                                })(<InputNumber min={0} max={maxSeats.vertical} />)}
                            </Item>
                        </Col>

                        <Col span={12}>
                            <Item label={tTable("right")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                                {form(RectTablePropertiesEnum.SeatsQuantityRight, {
                                    initialValue: seatsQuantity.right ?? 0,
                                })(<InputNumber min={0} max={maxSeats.vertical} />)}
                            </Item>
                        </Col>

                    </Row>
                </Col>
            </Row>

            <Row>
                <Col span={24} className="mb-1 mt-2">
                    <h3><b>{tTable("tableSize")}</b></h3>
                </Col>
                <Col span={24} className="mb-1">
                    <Item label={tTable("width")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RectTablePropertiesEnum.TableWidth, {
                            initialValue: table.width ?? 0,
                        })(<InputNumber min={0} />)}
                    </Item>

                    <Item label={tTable("heigth")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RectTablePropertiesEnum.TableHeight, {
                            initialValue: table.height ?? 0,
                        })(<InputNumber min={0} />)}
                    </Item>
                </Col>
            </Row>

            <Row>
                <Col span={24} className="mb-1 mt-2">
                    <h3><b>{tPropertyBar("sectionLabeling")}</b></h3>
                </Col>
                <Col span={24} className="mb-1">
                    <Item label={tPropertyBar("label")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RectTablePropertiesEnum.SectionLabel, {
                            initialValue: label ?? "",
                        })(<Input />)}
                    </Item>
                </Col>
            </Row>

            <Row>
                <Col span={24} className="mb-1 mt-2">
                    <h3><b>{tPropertyBar("tableLabeling")}</b></h3>
                </Col>
                <Col span={24} className="mb-1">
                    <Item label={tPropertyBar("label")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RectTablePropertiesEnum.TableLabelingLabel, {
                            initialValue: tableLabeling.label ?? "",
                        })(<Input />)}
                    </Item>
                    <Item label={tPropertyBar("displayedLabel")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RectTablePropertiesEnum.TableLabelingDisplayedLabel, {
                            initialValue: tableLabeling.displayedLabel ?? "",
                        })(<Input />)}
                    </Item>
                    <Item label={tTable("show")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RectTablePropertiesEnum.TableLabelingVisible, {
                            valuePropName: "checked",
                            initialValue: tableLabeling.visible ?? false,
                        })(<Switch />)}
                    </Item>
                </Col>
            </Row>

            <Row>
                <Col span={24} className="mb-1 mt-2">
                    <h3><b>{tPropertyBar("seatLabeling")}</b></h3>
                </Col>
                <Col span={24} className="mb-1">
                    <Item label={tPropertyBar("distribution")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RectTablePropertiesEnum.SeatLabelingType, {
                            initialValue: seatLabeling.key ?? SeatDistribution.NONE,
                        })(
                            <DistributionSelect distributionList={SEAT_LABEL_OPTIONS} />
                        )}
                    </Item>
                    {seatDistribution !== SeatDistribution.NONE &&
                        <Item label={tPropertyBar("startsAt")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                            {form(RectTablePropertiesEnum.SeatLabelingStartsAt, {
                                initialValue: seatLabeling.startsAt ?? 1,
                            })(<StartsAtInput distributionKey={seatDistribution} />)}
                        </Item>
                    }
                </Col>
            </Row>

            <Row>
                <Col span={24} className="mb-1 mt-2">
                    <h3><b>{tTable("miscellaneous")}</b></h3>
                </Col>
                <Col span={24} className="mb-2">
                    <Item label={tTable("selectionType")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RectTablePropertiesEnum.SelectionType, {
                            initialValue: selectionType ?? SelectionTypeEnum.bySeat,
                        })(<Select options={SELECTION_OPTIONS}>
                            {SELECTION_OPTIONS.map(({ value, label }) => (
                                <Option key={value} value={value}>
                                    {label}
                                </Option>
                            ))}
                        </Select>)}
                    </Item>
                </Col>
            </Row>
        </>
    );
};

export { RectTablePropertiesPanel };
